/**
 * S06 · Voice recording — live MediaRecorder session. Stop writes the
 * blob to OPFS via `createVoiceCapture`, then navigates to the review
 * screen (`/capture/voice/review/$captureId`) so the user can save,
 * trash, or re-record before the capture enters the timeline.
 */
import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, GestureBar, I, Ico, StatusBar, T, Waveform } from '@/components/primitives';
import { useVoiceRecorder } from '@/lib/device/useVoiceRecorder';
import { useGeolocation } from '@/lib/device/useGeolocation';
import { useCaptureSessionStore, useCreateVoiceMutation } from '@kapture/client-core';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { SubjectChip } from '@/components/SubjectChip';

export const Route = createFileRoute('/sessions/$sessionId/capture/voice')({
  component: VoiceRecordingPage,
  staticData: { mobile: true, fullscreen: true },
});

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hundredths = Math.floor((ms % 1000) / 10);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

function VoiceRecordingPage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/capture/voice">
      <VoiceRecordingContent />
    </LayoutOrLeaf>
  );
}

function VoiceRecordingContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const recorder = useVoiceRecorder();
  const geo = useGeolocation();
  const createVoice = useCreateVoiceMutation();
  // Set by a `voice` form field that sent us here; null when the dial did.
  const linkedFieldId = useCaptureSessionStore((st) => st.linkedFieldId);
  const setLinkedField = useCaptureSessionStore((st) => st.setLinkedField);

  useEffect(() => {
    void recorder.start();
    void geo.request({ timeout: 4000 }).catch(() => null);
    // teardown is handled inside useVoiceRecorder
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    recorder.reset();
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  async function finish() {
    const result = await recorder.stop();
    if (!result) {
      // nothing captured — bail back to timeline
      navigate({ to: '/sessions/$sessionId', params: { sessionId } });
      return;
    }
    const capture = await createVoice.mutateAsync({
      sessionId,
      media: result.blob,
      byteSize: result.blob.size,
      // Explicit now rather than sniffed from `blob.type`: the container
      // differs by platform, and the shared repository must not guess.
      extension: result.mimeType.includes('ogg') ? 'ogg' : 'webm',
      // The recorder's own answer, carried through to the upload. Reading it
      // back off the stored file gives `video/webm` in Chromium, and the
      // server refuses that 415 — which the queue treats as terminal, so the
      // note died on the device instead of syncing.
      mime: result.mimeType.split(';')[0] || 'audio/webm',
      durationMs: result.durationMs,
      gps: geo.point ?? null,
      linkedFieldId,
    });
    // Consumed; see the same call in the photo route.
    setLinkedField(null);
    navigate({
      to: '/sessions/$sessionId/capture/voice/review/$captureId',
      params: { sessionId, captureId: capture.id },
    });
  }

  async function discard() {
    await recorder.stop().catch(() => null);
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  const waveActive = Math.min(1, (recorder.elapsedMs % 8000) / 8000);
  const degraded =
    recorder.status === 'denied' ||
    recorder.status === 'unsupported' ||
    recorder.status === 'error';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: T.hudBg,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
      }}
    >
      <StatusBar dark />
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <button type="button" onClick={close} aria-label="Close" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
            minWidth: 0,
          }}
        >
          <Chip tone="hud" size="sm">
            <span style={{ fontFamily: T.mono, letterSpacing: '0.06em' }}>
              REC ·{' '}
              {sessionId
                .replace(/[^a-z0-9]/gi, '')
                .slice(0, 8)
                .toUpperCase()}{' '}
              · voice
            </span>
          </Chip>
          <SubjectChip />
        </div>
        <div style={{ width: 36 }} aria-hidden />
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 20px',
          gap: 16,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 100,
              background:
                recorder.status === 'recording' ? 'rgba(220,38,38,0.18)' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${recorder.status === 'recording' ? T.danger : 'rgba(255,255,255,0.2)'}`,
              color: '#fff',
              fontSize: 11,
              fontFamily: T.font,
              fontWeight: 600,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 4,
                background: recorder.status === 'recording' ? T.danger : 'rgba(255,255,255,0.4)',
                // The label next to this dot always states the true recording
                // state; the pulse is reinforcement and index.css stills it
                // under prefers-reduced-motion.
                animation:
                  recorder.status === 'recording' ? 'kap-pulse 1.4s ease-in-out infinite' : 'none',
              }}
            />
            {recorder.status === 'recording'
              ? 'REC'
              : recorder.status === 'requesting'
                ? 'Requesting mic…'
                : recorder.status === 'stopping'
                  ? 'Saving…'
                  : degraded
                    ? 'Unavailable'
                    : 'Idle'}
          </div>
          <div
            style={{
              fontSize: 40,
              fontWeight: 300,
              fontFamily: T.mono,
              color: '#fff',
              marginTop: 12,
              letterSpacing: '0.02em',
            }}
          >
            {formatTime(recorder.elapsedMs).slice(0, 5)}
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>
              {formatTime(recorder.elapsedMs).slice(5)}
            </span>
          </div>
        </div>

        <Waveform
          bars={48}
          active={waveActive}
          color={T.brand}
          bg="rgba(255,255,255,0.18)"
          h={80}
          animated={recorder.status === 'recording'}
        />

        <div
          style={{
            padding: 16,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Ico d={I.sparkle} size={13} fill={T.brand} stroke={T.brand} />
            <span
              style={{
                fontSize: 10,
                color: 'rgba(255,255,255,0.55)',
                fontFamily: T.mono,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Transcript
            </span>
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.75)',
              fontFamily: T.font,
              lineHeight: 1.5,
              minHeight: 56,
            }}
          >
            {degraded
              ? (recorder.error ?? 'Microphone unavailable in this browser.')
              : 'Nothing is transcribed on the device — the recording is stored here and transcribed after it uploads. You can read it, and correct it, on the review screen.'}
          </div>
        </div>

        {degraded && (
          <Btn tone="light" size="md" full onClick={discard}>
            Back to session
          </Btn>
        )}
      </div>

      <div
        style={{
          padding: '12px 20px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          type="button"
          onClick={discard}
          aria-label="Discard recording"
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            background: 'rgba(255,255,255,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <Ico d={I.trash} size={18} />
        </button>
        <button
          type="button"
          onClick={() => void finish()}
          disabled={recorder.status !== 'recording' || createVoice.isPending}
          aria-label="Stop recording"
          style={{
            width: 74,
            height: 74,
            borderRadius: 37,
            background: T.danger,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 0 6px rgba(220,38,38,0.22)',
            color: '#fff',
            border: 'none',
            cursor: recorder.status === 'recording' ? 'pointer' : 'not-allowed',
            opacity: recorder.status === 'recording' ? 1 : 0.55,
          }}
        >
          <Ico d={I.stop} size={24} />
        </button>
        <button
          type="button"
          onClick={() => void finish()}
          disabled={recorder.status !== 'recording' || createVoice.isPending}
          aria-label="Stop and save"
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            background: T.brand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: T.brandInk,
            border: 'none',
            cursor: recorder.status === 'recording' ? 'pointer' : 'not-allowed',
            opacity: recorder.status === 'recording' ? 1 : 0.5,
          }}
        >
          <Ico d={I.check} size={20} sw={2.4} />
        </button>
      </div>
      <GestureBar dark />
    </div>
  );
}

const hudIconBtn = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: 'rgba(255,255,255,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
} as const;
